import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Legal() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [accepted, setAccepted] = useState({});

  const load = async () => {
    const { data } = await api.get("/legal/documents");
    setDocs(data.documents);
    if (user) {
      try {
        const { data: acc } = await api.get("/legal/acceptances/me");
        setAccepted(Object.fromEntries(acc.map((a) => [`${a.doc_type}:${a.version}`, true])));
      } catch { /* anonymous */ }
    }
  };
  useEffect(() => { load(); }, [user]); // eslint-disable-line

  const open = async (docType) => {
    const { data } = await api.get(`/legal/documents/${docType}`);
    setActive(data);
  };

  const accept = async () => {
    try {
      await api.post("/legal/accept", { doc_type: active.doc_type, version: active.version });
      toast.success("Acceptance recorded");
      setAccepted({ ...accepted, [`${active.doc_type}:${active.version}`]: true });
    } catch { toast.error("Could not record acceptance"); }
  };

  return (
    <div className="space-y-6" data-testid="legal-root">
      <div>
        <h1 className="text-2xl font-display font-semibold">Legal & Disclosures</h1>
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mt-2" data-testid="legal-placeholder-notice">
          These documents are engineering placeholders pending counsel review. They are not
          legal advice and do not represent TradePilot as legally cleared to operate.
        </p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2">
          {docs.map((d) => (
            <Card key={d.doc_type} className="p-3 cursor-pointer hover:border-brand transition-colors"
                  onClick={() => open(d.doc_type)} data-testid={`legal-doc-${d.doc_type}`}>
              <p className="font-medium text-sm">{d.title}</p>
              <div className="flex gap-2 mt-1">
                <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">v{d.version}</Badge>
                {d.placeholder && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">placeholder</Badge>}
                {accepted[`${d.doc_type}:${d.version}`] && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">accepted</Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
        <Card className="md:col-span-2 p-5">
          {active ? (
            <div data-testid="legal-doc-view">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold">{active.title} <span className="text-gray-400 text-xs">v{active.version}</span></h2>
                {user && active.requires_acceptance && !accepted[`${active.doc_type}:${active.version}`] && (
                  <Button size="sm" onClick={accept} data-testid="legal-accept-btn">I acknowledge</Button>
                )}
              </div>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap max-h-[28rem] overflow-auto">{active.content}</pre>
            </div>
          ) : <p className="text-sm text-gray-500">Select a document to read it.</p>}
        </Card>
      </div>
    </div>
  );
}
